import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Ticket, ShoppingBag, DollarSign, Trophy } from "lucide-react";

export default function AdminDashboard() {
  const [raffles, setRaffles] = useState([]);
  const [jerseys, setJerseys] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [r, j, o] = await Promise.all([
          api.get("/raffles"),
          api.get("/jerseys"),
          api.get("/orders"),
        ]);
        setRaffles(r.data);
        setJerseys(j.data);
        setOrders(o.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const paidOrders = orders.filter(o => o.status === "paid");
  const revenue = paidOrders.reduce((s, o) => s + (o.amount || 0), 0);

  const cards = [
    { label: "Active Raffles", value: raffles.filter(r => r.status === "active").length, icon: Ticket },
    { label: "Jerseys Listed", value: jerseys.length, icon: ShoppingBag },
    { label: "Paid Orders", value: paidOrders.length, icon: Trophy },
    { label: "Revenue (£)", value: revenue.toFixed(2), icon: DollarSign },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div>
        <div className="font-mono-retro text-xs tracking-[0.4em] text-[#C8A96A] uppercase">Match Report</div>
        <h1 className="font-display text-4xl text-[#F2EBDD] mt-1">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="border border-[#332D2B] bg-[#1C1816] p-5">
            <div className="flex items-center justify-between">
              <c.icon size={18} className="text-[#C8A96A]" />
              <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">{c.label}</div>
            </div>
            <div className="font-display text-5xl text-[#F2EBDD] mt-3">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-[#332D2B] bg-[#1C1816] p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl text-[#F2EBDD]">Latest Raffles</h3>
            <Link to="/admin/raffles" className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase">Manage →</Link>
          </div>
          <ul className="mt-5 space-y-3">
            {raffles.slice(0, 5).map((r) => (
              <li key={r.id} className="flex justify-between items-center border-b border-[#332D2B] pb-2">
                <span className="text-[#F2EBDD]">{r.title}</span>
                <span className="font-mono-retro text-[10px] tracking-[0.25em] text-[#A8A39D] uppercase">{r.tickets_sold}/{r.total_tickets}</span>
              </li>
            ))}
            {raffles.length === 0 && <div className="text-[#A8A39D] text-sm">No raffles yet.</div>}
          </ul>
        </div>

        <div className="border border-[#332D2B] bg-[#1C1816] p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl text-[#F2EBDD]">Latest Orders</h3>
            <Link to="/admin/orders" className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase">View All →</Link>
          </div>
          <ul className="mt-5 space-y-3">
            {orders.slice(0, 5).map((o) => (
              <li key={o.id} className="flex justify-between items-center border-b border-[#332D2B] pb-2">
                <span className="text-[#F2EBDD] truncate max-w-[60%]">{o.item_name}</span>
                <span className={`font-mono-retro text-[10px] tracking-[0.25em] uppercase ${o.status === "paid" ? "text-[#C8A96A]" : "text-[#A8A39D]"}`}>
                  £{o.amount?.toFixed(2)} · {o.status}
                </span>
              </li>
            ))}
            {orders.length === 0 && <div className="text-[#A8A39D] text-sm">No orders yet.</div>}
          </ul>
        </div>
      </div>
    </div>
  );
}
